/**
 * Blog preview mirroring BorderGaritaPublicApp/components/BlogTemplatePage.js styles.
 * Uses inline styles so Admin (no Tailwind) matches the public Next render.
 */
import {
  formatHoursScheduleLines,
  getPlaceOpenStatus,
  normalizeHoursSchedule,
} from './placeHours';
import { resolveBlogCategory, getBlogCategoryIconSrc, getBlogCategoryLabel } from './blogCategories';

function slugify(text = '') {
  return String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function sectionAnchor(section) {
  if (section?.data?.anchor) return section.data.anchor;
  const data = section?.data || {};
  let text = '';
  switch (section?.type) {
    case 'place_block':
      text = data.title;
      break;
    case 'image':
      text = data.caption || data.alt;
      break;
    case 'rich_text':
    case 'paragraph':
      text = String(data.html_updated || data.html || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      break;
    default:
      text = data.text;
  }
  return slugify(text || section?.id || 'section');
}

function sectionShowsInToc(section, template) {
  const data = section?.data || {};
  if (typeof data.show_in_toc === 'boolean') return data.show_in_toc;
  // Legacy blogs without the flag: headings + template_2 place blocks
  if (section?.type === 'heading' && data.text) return true;
  if (template === 'template_2' && section?.type === 'place_block' && data.title) {
    return true;
  }
  return false;
}

function tocLabel(section) {
  const data = section?.data || {};
  switch (section?.type) {
    case 'heading':
      return (data.text || '').trim();
    case 'place_block':
      return (data.title || '').trim();
    case 'quote': {
      const text = String(data.text || '').trim();
      return text.length > 80 ? `${text.slice(0, 77)}…` : text;
    }
    case 'image':
      return (data.caption || data.alt || '').trim();
    case 'rich_text':
    case 'paragraph': {
      const plain = String(data.html_updated || data.html || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return plain.length > 80 ? `${plain.slice(0, 77)}…` : plain;
    }
    default:
      return '';
  }
}

function clampHeadingLevel(level) {
  const n = Number(level);
  if (!Number.isFinite(n)) return 2;
  return Math.min(6, Math.max(2, Math.round(n)));
}

const HEADING_PREVIEW_STYLES = {
  2: { fontSize: 28 },
  3: { fontSize: 22 },
  4: { fontSize: 18 },
  5: { fontSize: 16 },
  6: { fontSize: 14 },
};

function sectionHtml(data = {}) {
  return data.html_updated || data.html || '';
}

function buildTocItems(sections, template) {
  const items = [];
  for (const section of sections || []) {
    if (!sectionShowsInToc(section, template)) continue;
    const label = tocLabel(section);
    if (!label) continue;
    items.push({ label, anchor: sectionAnchor(section) });
  }
  return items;
}

function TocSidebar({ items, variant = 'template_1' }) {
  if (!items.length) return null;
  const isT2 = variant === 'template_2';
  return (
    <aside style={{ position: 'sticky', top: 24, alignSelf: 'start' }}>
      <div
        style={{
          borderRadius: 16,
          background: isT2 ? '#E8F5F0' : '#F7F7FB',
          border: isT2 ? '1px solid #d1fae5' : '1px solid #e5e7eb',
          padding: 20,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: '#0D0D65',
            marginBottom: 16,
          }}
        >
          EN ESTE ARTÍCULO
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((item, i) => (
            <li key={`${item.anchor}-${i}`} style={{ marginBottom: 12 }}>
              <a
                href={`#preview-${item.anchor}`}
                style={{
                  display: 'block',
                  color: '#1a1a2e',
                  textDecoration: 'none',
                  fontSize: 14,
                  lineHeight: 1.35,
                }}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function resolvePlaceLink(links, key, legacyKey) {
  const entry = links?.[key];
  if (entry && typeof entry === 'object' && (entry.url || entry.alt)) {
    return {
      url: entry.url || '',
      alt: entry.alt || '',
    };
  }
  if (typeof entry === 'string' && entry) {
    return { url: entry, alt: '' };
  }
  if (legacyKey && typeof links?.[legacyKey] === 'string' && links[legacyKey]) {
    return { url: links[legacyKey], alt: '' };
  }
  return null;
}

const PLACE_LINK_META = [
  { key: 'maps', label: 'Maps', icon: '/place-maps.svg', legacy: 'maps_url' },
  { key: 'website', label: 'Website', icon: '/place-website.svg', legacy: 'website_url' },
  { key: 'facebook', label: 'Facebook', icon: '/place-facebook.svg' },
  { key: 'instagram', label: 'Instagram', icon: '/place-instagram.svg' },
  { key: 'tiktok', label: 'TikTok', icon: '/place-tiktok.svg' },
];

function PlaceBlock({ data = {}, anchor }) {
  const links = PLACE_LINK_META.map((meta) => {
    const resolved = resolvePlaceLink(data.links, meta.key, meta.legacy);
    if (!resolved?.url) return null;
    return {
      ...meta,
      href: resolved.url,
      alt: resolved.alt || meta.label,
    };
  }).filter(Boolean);

  const schedule = normalizeHoursSchedule(data.hours_schedule);
  const scheduleLines = formatHoursScheduleLines(data.hours_schedule);
  const openStatus = getPlaceOpenStatus(data.hours_schedule);
  const hoursDisplay =
    scheduleLines.length > 0
      ? scheduleLines.join('\n')
      : data.hours || '—';

  return (
    <section id={anchor ? `preview-${anchor}` : undefined} style={{ marginBottom: 40 }}>
      <div
        style={{
          height: 8,
          width: '100%',
          borderRadius: 999,
          background: 'rgba(13,13,101,0.1)',
          marginBottom: 20,
        }}
      />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        {data.title && (
          <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: '#0D0D65' }}>
            {data.title}
          </h2>
        )}
        {links.length > 0 && (
          <div style={{ display: 'flex', gap: 8 }}>
            {links.map((link) => (
              <a
                key={link.key}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.alt}
                title={link.alt}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#374151',
                  textDecoration: 'none',
                }}
              >
                <img src={link.icon} alt="" width={16} height={16} aria-hidden />
              </a>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          borderRadius: 16,
          border: '1px solid #e5e7eb',
          background: '#fff',
          padding: 24,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
          {(data.chips || []).map((chip, i) => (
            <span
              key={i}
              style={{
                borderRadius: 999,
                background: '#f3f4f6',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
                color: '#1f2937',
              }}
            >
              {chip}
            </span>
          ))}
          {openStatus && (
            <span
              style={{
                borderRadius: 999,
                background: openStatus.open ? '#ecfdf5' : '#fff1f2',
                color: openStatus.open ? '#065f46' : '#9f1239',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
              title={schedule.timezone}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: openStatus.open ? '#10b981' : '#f43f5e',
                  display: 'inline-block',
                }}
              />
              {openStatus.label}
            </span>
          )}
          {!openStatus && data.open_status ? (
            <span
              style={{
                borderRadius: 999,
                background: '#ecfdf5',
                color: '#065f46',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#10b981',
                  display: 'inline-block',
                }}
              />
              {data.open_status}
            </span>
          ) : null}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px 32px',
            fontSize: 14,
            marginBottom: 20,
          }}
        >
          {[
            ['Dirección', data.address, true],
            ['Horario', hoursDisplay, true],
            ['Desde la garita', data.from_garita],
            ['Especialidad', data.specialty],
          ].map(([label, value, pre]) => (
            <div key={label}>
              <div
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: '#6b7280',
                  marginBottom: 4,
                }}
              >
                {label}
              </div>
              <div
                style={{
                  color: '#1f2937',
                  lineHeight: 1.5,
                  whiteSpace: pre ? 'pre-line' : 'normal',
                }}
              >
                {value || '—'}
              </div>
              {label === 'Horario' && scheduleLines.length > 0 && data.hours ? (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {data.hours}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {data.before_you_go && (
          <div
            style={{
              borderRadius: 12,
              background: '#F7F7FB',
              padding: '16px 18px 16px 16px',
              display: 'flex',
              gap: 14,
              alignItems: 'stretch',
            }}
          >
            <div
              style={{
                width: 3,
                borderRadius: 2,
                background: '#2020A0',
                flexShrink: 0,
                alignSelf: 'stretch',
              }}
              aria-hidden
            />
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1.2px',
                  color: '#2020A0',
                  textTransform: 'uppercase',
                }}
              >
                Antes de ir
              </div>
              <div style={{ fontSize: 14, lineHeight: '21px', color: '#555560' }}>
                {data.before_you_go}
              </div>
            </div>
          </div>
        )}
      </div>

      {sectionHtml(data) ? (
        <div
          style={{
            fontSize: 17,
            lineHeight: 1.75,
            color: '#374151',
            marginTop: 20,
          }}
          dangerouslySetInnerHTML={{ __html: sectionHtml(data) }}
        />
      ) : null}
    </section>
  );
}

function SectionPreview({ section, template }) {
  const data = section?.data || {};
  const anchor = sectionAnchor(section);
  const needsId =
    Boolean(data.anchor) || sectionShowsInToc(section, template);
  const previewId = needsId ? `preview-${anchor}` : undefined;

  switch (section?.type) {
    case 'heading': {
      const level = clampHeadingLevel(data.level);
      const Tag = `h${level}`;
      const html = sectionHtml(data);
      return (
        <section style={{ marginBottom: 24 }}>
          <Tag
            id={previewId}
            style={{
              ...HEADING_PREVIEW_STYLES[level],
              fontWeight: 700,
              marginTop: 40,
              marginBottom: html ? 12 : 16,
              color: '#0D0D65',
              scrollMarginTop: 24,
            }}
          >
            {data.text}
          </Tag>
          {html ? (
            <div
              style={{
                fontSize: 17,
                lineHeight: 1.75,
                color: '#374151',
              }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : null}
        </section>
      );
    }
    case 'paragraph':
      return (
        <div
          id={previewId}
          style={{
            fontSize: 17,
            lineHeight: 1.75,
            color: '#374151',
            marginBottom: 24,
            scrollMarginTop: 24,
          }}
          dangerouslySetInnerHTML={{ __html: sectionHtml(data) }}
        />
      );
    case 'rich_text':
      return (
        <div
          id={previewId}
          style={{
            fontSize: 17,
            lineHeight: 1.75,
            color: '#374151',
            marginBottom: 24,
            scrollMarginTop: 24,
          }}
          dangerouslySetInnerHTML={{ __html: data.html || '' }}
        />
      );
    case 'quote':
      return (
        <blockquote
          id={previewId}
          style={{
            borderLeft: '4px solid #0D0D65',
            paddingLeft: 20,
            margin: '32px 0',
            fontSize: 18,
            fontStyle: 'italic',
            color: '#374151',
            scrollMarginTop: 24,
          }}
        >
          {data.text}
        </blockquote>
      );
    case 'image':
      return (
        <figure
          id={previewId}
          style={{ margin: '32px 0', position: 'relative', scrollMarginTop: 24 }}
        >
          {data.pinned && (
            <span
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                background: 'rgba(255,255,255,0.95)',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
              }}
            >
              📌
            </span>
          )}
          {data.url ? (
            <img
              src={data.url}
              alt={data.alt || ''}
              style={{
                width: '100%',
                aspectRatio: '16 / 10',
                objectFit: 'cover',
                borderRadius: 12,
                display: 'block',
                background: '#f3f4f6',
              }}
            />
          ) : null}
          {(data.caption || data.credit) && (
            <figcaption
              style={{
                textAlign: 'center',
                fontSize: 14,
                color: '#6b7280',
                marginTop: 12,
              }}
            >
              {data.caption && <div>{data.caption}</div>}
              {data.credit && (
                <div
                  style={{ fontSize: 12, marginTop: 4 }}
                  dangerouslySetInnerHTML={{ __html: data.credit }}
                />
              )}
            </figcaption>
          )}
        </figure>
      );
    case 'place_block':
      return <PlaceBlock data={data} anchor={needsId ? anchor : undefined} />;
    default:
      return null;
  }
}

const PreviewBlog = ({
  sections = [],
  hero = {},
  template = 'template_1',
  faqs_section,
  full_slug = '',
}) => {
  const isT2 = template === 'template_2';
  const tocItems = buildTocItems(sections, template);
  const showToc = tocItems.length > 0;
  const desc =
    hero?.description && hero.description.length > 220
      ? `${hero.description.slice(0, 220).trim()}…`
      : hero?.description;
  const category = resolveBlogCategory(hero?.category);
  const categoryIconSrc = getBlogCategoryIconSrc(category?.icon || category?.code);
  const categoryLabel = getBlogCategoryLabel(category, 'ES');

  const article = (
    <article style={{ minWidth: 0 }}>
      {sections.length > 0 ? (
        sections.map((section, i) => (
          <SectionPreview key={section.id || i} section={section} template={template} />
        ))
      ) : !hero?.description ? (
        <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>
          Este artículo aún no tiene secciones de contenido.
        </p>
      ) : null}
    </article>
  );

  const toc = showToc ? <TocSidebar items={tocItems} variant={template} /> : null;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        background: '#fff',
        borderRadius: 8,
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}
    >
      {/* Chrome bar */}
      <div
        style={{
          background: '#f1f3f4',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={dot('#ff5f56')} />
        <span style={dot('#ffbd2e')} />
        <span style={dot('#27c93f')} />
        <div
          style={{
            flex: 1,
            textAlign: 'center',
            fontSize: 13,
            color: '#888',
            fontFamily: 'monospace',
            background: '#fff',
            borderRadius: 4,
            padding: '4px 12px',
            border: '1px solid #e0e0e0',
            marginLeft: 8,
          }}
        >
          bordergarita.com{full_slug || '/blog'}
        </div>
      </div>

      {/* Fake header strip */}
      <div
        style={{
          background: '#0D0D65',
          color: 'rgba(255,255,255,0.75)',
          fontSize: 11,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
        }}
      >
        Tiempos de cruce en vivo · Actualizados cada 5 min
      </div>
      <div
        style={{
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #f3f4f6',
        }}
      >
        <img
          src="/logo.svg"
          alt="Border Garita"
          width={140}
          height={40}
          style={{ display: 'block', objectFit: 'contain' }}
        />
      </div>

      {/* Hero — same structure as BlogTemplatePage */}
      <section
        style={{
          padding: '32px 32px 40px',
          borderBottom: '1px solid #f3f4f6',
        }}
      >
        {hero?.breadcrumb && (
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '1.2px',
              color: '#9ca3af',
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            {hero.breadcrumb}
          </p>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: hero?.image?.url ? '1fr 1fr' : '1fr',
            gap: 48,
            alignItems: 'center',
          }}
        >
          <div>
            {categoryLabel && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 999,
                  border: '1px solid #e4e4ed',
                  background: '#fff',
                  padding: '8px 16px 8px 14px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#1a1a2e',
                  marginBottom: 16,
                }}
              >
                {categoryIconSrc && (
                  <img
                    src={categoryIconSrc}
                    alt=""
                    width={18}
                    height={18}
                    style={{ display: 'block', flexShrink: 0 }}
                  />
                )}
                {categoryLabel}
              </span>
            )}
            <h1
              style={{
                fontSize: 42,
                lineHeight: 1.15,
                fontWeight: 700,
                color: '#0D0D65',
                margin: '0 0 16px',
              }}
            >
              {hero?.title || 'Blog'}
            </h1>
            {desc && (
              <p
                style={{
                  fontSize: 18,
                  color: '#4b5563',
                  lineHeight: 1.6,
                  margin: '0 0 24px',
                  maxWidth: 520,
                }}
              >
                {desc}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              {[1, 2, 3].map((n) => (
                <span
                  key={n}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    display: 'inline-block',
                  }}
                />
              ))}
            </div>
          </div>

          {hero?.image?.url && (
            <div
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '5 / 4',
                borderRadius: 16,
                overflow: 'hidden',
                background: '#f3f4f6',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}
            >
              <img
                src={hero.image.url}
                alt={hero.image.alt || ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <main style={{ padding: '40px 32px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: showToc
              ? isT2
                ? 'minmax(0,1fr) 300px'
                : '280px minmax(0,1fr)'
              : '1fr',
            gap: 48,
            maxWidth: showToc ? 'none' : 720,
          }}
        >
          {isT2 ? (
            <>
              {article}
              {toc}
            </>
          ) : (
            <>
              {toc}
              {article}
            </>
          )}
        </div>

        {faqs_section?.items?.length > 0 && (
          <div
            style={{
              marginTop: 56,
              borderTop: '1px solid #f3f4f6',
              paddingTop: 32,
            }}
          >
            <h3 style={{ fontSize: 24, fontWeight: 700, color: '#0D0D65' }}>
              {faqs_section.h3 || 'FAQs'}
            </h3>
            {faqs_section.p && (
              <p style={{ color: '#4b5563', marginBottom: 16 }}>{faqs_section.p}</p>
            )}
            {faqs_section.items.map((faq, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <strong style={{ color: '#111827' }}>{faq.question}</strong>
                {faq.answer && (
                  <div
                    style={{
                      whiteSpace: 'pre-line',
                      color: '#4b5563',
                      marginTop: 4,
                    }}
                  >
                    {faq.answer}
                  </div>
                )}
                {(faq.answer_html_updated || faq.answer_html) && (
                  <div
                    style={{
                      color: '#4b5563',
                      marginTop: 4,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: faq.answer_html_updated || faq.answer_html,
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <div style={{ background: '#0D0D65', height: 80 }} />
    </div>
  );
};

function dot(color) {
  return {
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: color,
    display: 'inline-block',
    border: '1px solid #e0e0e0',
  };
}

export default PreviewBlog;
